import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { verifyBVN } from "src/api/settings";
import { TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { showSuccessToast } from "src/lib/utils";

const validationSchema = Yup.object().shape({
	bvn: Yup.string().required("BVN is required").length(11, "A valid BVN is 11 digits"),
});

interface BvnFormValues {
	businessUuid?: string;
	bvn: string;
}

const UpgradeToTier2 = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const initialValues: BvnFormValues = { businessUuid: user?.business_uuid, bvn: "" };

	const { mutate, isLoading } = useMutation(verifyBVN, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["account-tiers", "business"]);
			queryClient.invalidateQueries(["user"]);
			closePortal();
		},
	});

	return (
		<div className="tw-grid tw-gap-5">
			<p className="tw-rounded-[10px] tw-bg-teal-tint tw-px-4 tw-py-3 tw-text-[13.5px] tw-text-text">
				Once verified, your account is upgraded to tier 2 — a ₦10,000.00 transaction limit and a maximum balance of ₦100,000,000.
			</p>

			<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
				{({ values, handleChange, handleSubmit, errors, touched }) => (
					<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
						<TextField
							label="BVN"
							name="bvn"
							inputMode="numeric"
							maxLength={11}
							placeholder="10013939130"
							value={values.bvn}
							error={touched.bvn && errors.bvn}
							onChange={handleChange}
							hint="Dial *384*1# to get your BVN"
							required
						/>

						<Button type="submit" variant="secondary" className="tw-w-full" disabled={isLoading}>
							{isLoading ? "Verifying..." : "Verify"}
						</Button>
					</form>
				)}
			</Formik>
		</div>
	);
};

export default UpgradeToTier2;
